import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import {
  Megaphone,
  Languages,
  Plus,
  Pencil,
  Trash2,
  RefreshCw,
  CheckCircle,
  Info,
  AlertTriangle,
  AlertCircle,
  Save,
  Send,
  FileText,
} from 'lucide-react';
import Button from '../common/Button';
import Input from '../common/Input';
import DateInput from '../common/DateInput';
import ConfirmDialog from '../common/ConfirmDialog';
import * as announcementService from '../../services/announcement';
import {
  applyVariables,
  EMPTY_TEMPLATE_VALUES,
  extractVariables,
  hasPeriodPart,
  localizeTimes,
  normalizeHourInput,
  usesPeriod,
} from '../../utils/announcementTemplate';
import { useAnnouncementStore } from '../../store/announcementStore';
import type {
  Announcement,
  AnnouncementLevel,
  AnnouncementPage,
  AnnouncementTemplate,
  TemplateValues,
  TemplateVariable,
} from '../../types';

const LEVELS: AnnouncementLevel[] = ['success', 'info', 'warning', 'danger'];

// Valeurs d'exemple de l'aide aux motifs. Figées pour que le tableau reste
// stable d'une session à l'autre ; seul le fuseau du lecteur fait varier le rendu.
const EXAMPLE_DAY = '2026-10-12';
const EXAMPLE_START_HOUR = '22:00';
const EXAMPLE_END_HOUR = '23:30';

const levelIcons: Record<AnnouncementLevel, typeof Info> = {
  success: CheckCircle,
  info: Info,
  warning: AlertTriangle,
  danger: AlertCircle,
};

interface FormState {
  level: AnnouncementLevel;
  contentFr: string;
  contentEn: string;
}

const EMPTY_FORM: FormState = {
  level: 'info',
  contentFr: '',
  contentEn: '',
};

const PAGE_SIZE = 10;

const EMPTY_PAGE: AnnouncementPage = { items: [], total: 0, page: 1, pageSize: PAGE_SIZE };

interface AnnouncementPanelProps {
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
}

export default function AnnouncementPanel({ onError, onSuccess }: AnnouncementPanelProps) {
  const { t, i18n } = useTranslation('admin');
  const refreshBanner = useAnnouncementStore((state) => state.fetchAnnouncement);

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  /** `null` = l'éditeur crée une nouvelle annonce ; sinon il modifie celle-ci. */
  const [editingId, setEditingId] = useState<string | null>(null);
  const [history, setHistory] = useState<AnnouncementPage>(EMPTY_PAGE);
  const [templates, setTemplates] = useState<AnnouncementTemplate[]>([]);
  const [translationEnabled, setTranslationEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const [editingTemplate, setEditingTemplate] =
    useState<{ id: string | null; draft: TemplateDraft } | null>(null);
  const [filling, setFilling] = useState<AnnouncementTemplate | null>(null);
  const [deletingTemplate, setDeletingTemplate] = useState<AnnouncementTemplate | null>(null);
  const [deletingAnnouncement, setDeletingAnnouncement] = useState<Announcement | null>(null);

  const load = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const response = await announcementService.getAnnouncementAdmin(page, PAGE_SIZE);
        if (response.success && response.data) {
          setHistory(response.data.announcements);
          setTemplates(response.data.templates);
          setTranslationEnabled(response.data.translationEnabled);
        }
      } catch {
        onError(t('announcement.loadError'));
      } finally {
        setLoading(false);
      }
    },
    [onError, t]
  );

  useEffect(() => {
    load();
  }, [load]);

  const resetEditor = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
  };

  /**
   * Publie l'annonce en cours d'édition.
   *
   * Le bandeau du shell est rechargé dans la foulée : sans cela, l'admin devrait
   * recharger la page pour constater l'effet de sa propre publication.
   */
  const handlePublish = async (activate: boolean) => {
    setBusy('save');
    try {
      const payload = { ...form, activate };
      const response = editingId
        ? await announcementService.updateAnnouncement(editingId, payload)
        : await announcementService.createAnnouncement(payload);

      if (response.success && response.data) {
        onSuccess(
          editingId
            ? t('announcement.updated')
            : activate
              ? t('announcement.publishedActive')
              : t('announcement.publishedDraft')
        );
        resetEditor();
        await Promise.all([load(1), refreshBanner()]);
      }
    } catch {
      onError(t('announcement.saveError'));
    } finally {
      setBusy(null);
    }
  };

  const handleToggleActive = async (announcement: Announcement) => {
    setBusy(announcement.id);
    try {
      const response = await announcementService.setAnnouncementActive(
        announcement.id,
        !announcement.isActive
      );
      if (response.success) {
        onSuccess(
          announcement.isActive ? t('announcement.deactivated') : t('announcement.activated')
        );
        await Promise.all([load(history.page), refreshBanner()]);
      }
    } catch {
      onError(t('announcement.saveError'));
    } finally {
      setBusy(null);
    }
  };

  const handleDeleteAnnouncement = async () => {
    if (!deletingAnnouncement) return;
    const target = deletingAnnouncement;
    setDeletingAnnouncement(null);
    setBusy(target.id);
    try {
      const response = await announcementService.deleteAnnouncement(target.id);
      if (response.success) {
        if (editingId === target.id) resetEditor();
        onSuccess(t('announcement.deleted'));
        // Une page qui se vide après suppression renvoie à la précédente.
        const lastPage = Math.max(1, Math.ceil((history.total - 1) / PAGE_SIZE));
        await Promise.all([load(Math.min(history.page, lastPage)), refreshBanner()]);
      }
    } catch {
      onError(t('announcement.deleteError'));
    } finally {
      setBusy(null);
    }
  };

  const handleEdit = (announcement: Announcement) => {
    setEditingId(announcement.id);
    setForm({
      level: announcement.level,
      contentFr: announcement.contentFr,
      contentEn: announcement.contentEn,
    });
    // L'éditeur est en haut de l'onglet : sans cela, le clic paraîtrait sans effet.
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleTranslate = async () => {
    setBusy('translate');
    try {
      const response = await announcementService.translate(form.contentFr, 'EN', 'FR');
      if (response.success && response.data) {
        setForm((prev) => ({ ...prev, contentEn: response.data!.html }));
        onSuccess(t('announcement.translated'));
      }
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      onError(message || t('announcement.translateError'));
    } finally {
      setBusy(null);
    }
  };

  const handleSaveTemplate = async (draft: TemplateDraft, id: string | null) => {
    setBusy('template');
    try {
      const response = id
        ? await announcementService.updateTemplate(id, draft)
        : await announcementService.createTemplate(draft);

      if (response.success && response.data) {
        const saved = response.data;
        setTemplates((prev) =>
          id ? prev.map((tpl) => (tpl.id === id ? saved : tpl)) : [saved, ...prev]
        );
        setEditingTemplate(null);
        onSuccess(id ? t('announcement.templates.updated') : t('announcement.templates.created'));
      }
    } catch {
      onError(t('announcement.templates.saveError'));
    } finally {
      setBusy(null);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!deletingTemplate) return;
    const target = deletingTemplate;
    setDeletingTemplate(null);
    setBusy('template');
    try {
      const response = await announcementService.deleteTemplate(target.id);
      if (response.success) {
        setTemplates((prev) => prev.filter((tpl) => tpl.id !== target.id));
        onSuccess(t('announcement.templates.deleted'));
      }
    } catch {
      onError(t('announcement.templates.deleteError'));
    } finally {
      setBusy(null);
    }
  };

  /** Insère le modèle rempli dans l'éditeur — sans publier : l'admin relit puis valide. */
  const handleApplyTemplate = (template: AnnouncementTemplate, values: TemplateValues) => {
    setForm((prev) => ({
      ...prev,
      level: template.level,
      contentFr: applyVariables(template.contentFr, values, 'fr'),
      contentEn: applyVariables(template.contentEn, values, 'en'),
    }));
    setFilling(null);
  };

  if (loading && history.items.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-base-muted">
        <RefreshCw className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  const pageCount = Math.max(1, Math.ceil(history.total / history.pageSize));
  const dateFormatter = new Intl.DateTimeFormat(i18n.language.startsWith('en') ? 'en-US' : 'fr-FR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  return (
    <div className="space-y-6">
      {/* ─── Éditeur ─── */}
      <section className="bg-white rounded-lg border p-6 space-y-5">
        <div className="flex items-start gap-3">
          <Megaphone className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-medium text-gray-900">
              {editingId ? t('announcement.editTitle') : t('announcement.bannerTitle')}
            </h3>
            <p className="text-sm text-gray-500">{t('announcement.bannerDesc')}</p>
          </div>
          {editingId && (
            <Button type="button" variant="ghost" size="sm" onClick={resetEditor}>
              {t('announcement.cancelEdit')}
            </Button>
          )}
        </div>

        <LevelSelector
          value={form.level}
          onChange={(level) => setForm((prev) => ({ ...prev, level }))}
        />

        <div className="space-y-2">
          <label htmlFor="announcement-fr" className="block text-sm font-medium text-gray-700">
            {t('announcement.contentFr')}
          </label>
          <textarea
            id="announcement-fr"
            value={form.contentFr}
            onChange={(e) => setForm((prev) => ({ ...prev, contentFr: e.target.value }))}
            rows={3}
            placeholder={t('announcement.contentPlaceholder')}
            className="input w-full font-mono text-sm"
          />
          <p className="text-xs text-gray-500">{t('announcement.allowedTags')}</p>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <label htmlFor="announcement-en" className="block text-sm font-medium text-gray-700">
              {t('announcement.contentEn')}
            </label>
            {translationEnabled && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleTranslate}
                isLoading={busy === 'translate'}
                disabled={!form.contentFr.trim() || busy !== null}
                leftIcon={<Languages className="w-4 h-4" />}
              >
                {busy === 'translate' ? t('announcement.translating') : t('announcement.translate')}
              </Button>
            )}
          </div>
          <textarea
            id="announcement-en"
            value={form.contentEn}
            onChange={(e) => setForm((prev) => ({ ...prev, contentEn: e.target.value }))}
            rows={3}
            className="input w-full font-mono text-sm"
          />
          <p className="text-xs text-gray-500">
            {translationEnabled
              ? t('announcement.emptyEnHint')
              : `${t('announcement.emptyEnHint')} ${t('announcement.translateUnavailable')}`}
          </p>
        </div>

        <AnnouncementPreview level={form.level} contentFr={form.contentFr} />

        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            onClick={() => handlePublish(true)}
            isLoading={busy === 'save'}
            disabled={busy !== null || !form.contentFr.trim()}
            leftIcon={<Send className="w-4 h-4" />}
          >
            {editingId ? t('announcement.saveAndActivate') : t('announcement.publishAndActivate')}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => handlePublish(false)}
            disabled={busy !== null || !form.contentFr.trim()}
            leftIcon={<Save className="w-4 h-4" />}
          >
            {t('announcement.saveWithoutActivating')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() =>
              setEditingTemplate({
                id: null,
                draft: {
                  name: '',
                  level: form.level,
                  contentFr: form.contentFr,
                  contentEn: form.contentEn,
                },
              })
            }
            disabled={busy !== null || !form.contentFr.trim()}
            leftIcon={<FileText className="w-4 h-4" />}
          >
            {t('announcement.templates.saveAsTemplate')}
          </Button>
        </div>
      </section>

      {/* ─── Historique ─── */}
      <section className="bg-white rounded-lg border p-6 space-y-4">
        <div>
          <h3 className="text-lg font-medium text-gray-900">{t('announcement.history')}</h3>
          <p className="text-sm text-gray-500">{t('announcement.historyDesc')}</p>
        </div>

        {history.items.length === 0 && (
          <p className="text-sm text-gray-500 py-4 text-center">{t('announcement.historyEmpty')}</p>
        )}

        <ul className="divide-y">
          {history.items.map((announcement) => (
            <li key={announcement.id} className="py-3 flex items-start gap-3 flex-wrap">
              <span
                className="announcement-dot mt-2 flex-shrink-0"
                data-level={announcement.level}
                aria-hidden="true"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-gray-900 truncate">
                    {stripTags(announcement.contentFr) || t('announcement.historyEmptyContent')}
                  </span>
                  {announcement.isActive && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      <CheckCircle className="w-3 h-3" />
                      {t('announcement.active')}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {dateFormatter.format(new Date(announcement.publishedAt))}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={announcement.isActive ? 'outline' : 'primary'}
                  onClick={() => handleToggleActive(announcement)}
                  isLoading={busy === announcement.id}
                  disabled={busy !== null}
                >
                  {announcement.isActive
                    ? t('announcement.deactivate')
                    : t('announcement.activate')}
                </Button>
                <button
                  type="button"
                  onClick={() => handleEdit(announcement)}
                  disabled={busy !== null}
                  className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-50"
                  aria-label={t('announcement.edit')}
                  title={t('announcement.edit')}
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setDeletingAnnouncement(announcement)}
                  disabled={busy !== null}
                  className="p-2 rounded-lg text-red-500 hover:bg-red-50 disabled:opacity-50"
                  aria-label={t('announcement.templates.delete')}
                  title={t('announcement.templates.delete')}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>

        {pageCount > 1 && (
          <div className="flex items-center justify-between gap-3 pt-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => load(history.page - 1)}
              disabled={history.page <= 1 || busy !== null}
            >
              {t('announcement.previousPage')}
            </Button>
            <span className="text-xs text-gray-500">
              {t('announcement.pageOf', { page: history.page, total: pageCount })}
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => load(history.page + 1)}
              disabled={history.page >= pageCount || busy !== null}
            >
              {t('announcement.nextPage')}
            </Button>
          </div>
        )}
      </section>

      {/* ─── Modèles ─── */}
      <section className="bg-white rounded-lg border p-6 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h3 className="text-lg font-medium text-gray-900">
              {t('announcement.templates.title')}
            </h3>
            <p className="text-sm text-gray-500">{t('announcement.templates.desc')}</p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              setEditingTemplate({
                id: null,
                draft: { name: '', level: 'info', contentFr: '', contentEn: '' },
              })
            }
            disabled={busy !== null}
            leftIcon={<Plus className="w-4 h-4" />}
          >
            {t('announcement.templates.new')}
          </Button>
        </div>

        {templates.length === 0 && (
          <p className="text-sm text-gray-500 py-4 text-center">
            {t('announcement.templates.empty')}
          </p>
        )}

        <ul className="divide-y">
          {templates.map((template) => (
            <li key={template.id} className="py-3 flex items-start gap-3 flex-wrap">
              <span
                className="announcement-dot mt-2 flex-shrink-0"
                data-level={template.level}
                aria-hidden="true"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">{template.name}</p>
                <p className="text-sm text-gray-500 truncate">{stripTags(template.contentFr)}</p>
                {template.variables.length > 0 && (
                  <p className="text-xs text-gray-400 mt-1">
                    {t('announcement.templates.variables')} :{' '}
                    {template.variables.map(describeVariable).join(', ')}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    template.variables.length > 0
                      ? setFilling(template)
                      : handleApplyTemplate(template, EMPTY_TEMPLATE_VALUES)
                  }
                  disabled={busy !== null}
                >
                  {t('announcement.templates.use')}
                </Button>
                <button
                  type="button"
                  onClick={() =>
                    setEditingTemplate({
                      id: template.id,
                      draft: {
                        name: template.name,
                        level: template.level,
                        contentFr: template.contentFr,
                        contentEn: template.contentEn,
                      },
                    })
                  }
                  disabled={busy !== null}
                  className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-50"
                  aria-label={t('announcement.templates.edit')}
                  title={t('announcement.templates.edit')}
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setDeletingTemplate(template)}
                  disabled={busy !== null}
                  className="p-2 rounded-lg text-red-500 hover:bg-red-50 disabled:opacity-50"
                  aria-label={t('announcement.templates.delete')}
                  title={t('announcement.templates.delete')}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      </section>

      {editingTemplate && (
        <TemplateEditorModal
          initial={editingTemplate.draft}
          isNew={editingTemplate.id === null}
          saving={busy === 'template'}
          translationEnabled={translationEnabled}
          onCancel={() => setEditingTemplate(null)}
          onSave={(draft) => handleSaveTemplate(draft, editingTemplate.id)}
          onTranslateError={onError}
        />
      )}

      {filling && (
        <TemplateFillModal
          template={filling}
          onCancel={() => setFilling(null)}
          onApply={(values) => handleApplyTemplate(filling, values)}
        />
      )}

      <ConfirmDialog
        isOpen={deletingTemplate !== null}
        onClose={() => setDeletingTemplate(null)}
        onConfirm={handleDeleteTemplate}
        title={t('announcement.templates.delete')}
        message={t('announcement.templates.deleteConfirm', { name: deletingTemplate?.name ?? '' })}
        variant="danger"
      />

      <ConfirmDialog
        isOpen={deletingAnnouncement !== null}
        onClose={() => setDeletingAnnouncement(null)}
        onConfirm={handleDeleteAnnouncement}
        title={t('announcement.templates.delete')}
        message={t('announcement.deleteConfirm')}
        variant="danger"
      />
    </div>
  );
}

// ============================================
// Sous-composants
// ============================================

/** Libellé court d'un champ de modèle, pour les listes récapitulatives. */
function describeVariable(variable: TemplateVariable): string {
  return variable.kind === 'text'
    ? variable.name
    : `${variable.moment}:${variable.part}`;
}

function stripTags(html: string): string {
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

function LevelSelector({
  value,
  onChange,
}: {
  value: AnnouncementLevel;
  onChange: (level: AnnouncementLevel) => void;
}) {
  const { t } = useTranslation('admin');

  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium text-gray-700 mb-2">
        {t('announcement.level')}
      </legend>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {LEVELS.map((level) => {
          const Icon = levelIcons[level];
          const selected = value === level;

          return (
            <button
              key={level}
              type="button"
              onClick={() => onChange(level)}
              aria-pressed={selected}
              className={clsx(
                'flex items-center gap-2 px-3 py-2 rounded-lg border text-sm text-left transition-colors',
                selected
                  ? 'border-primary-500 bg-primary-50 text-primary-800 font-medium'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              )}
            >
              <span className="announcement-dot" data-level={level} aria-hidden="true" />
              <Icon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
              {t(`announcement.levels.${level}`)}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

/** Aperçu rendu avec les styles réels du bandeau, pour éviter toute surprise. */
function AnnouncementPreview({
  level,
  contentFr,
}: {
  level: AnnouncementLevel;
  contentFr: string;
}) {
  const { t } = useTranslation('admin');
  const Icon = levelIcons[level];

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-gray-700">{t('announcement.preview')}</p>
      {contentFr.trim() ? (
        <div className="announcement-banner" data-level={level}>
          <Icon className="announcement-banner__icon w-5 h-5" aria-hidden="true" />
          <div
            className="announcement-banner__content"
            // Aperçu du contenu que l'admin vient de saisir, dans sa propre session.
            dangerouslySetInnerHTML={{ __html: localizeTimes(contentFr, 'fr') }}
          />
        </div>
      ) : (
        <p className="text-sm text-gray-400 italic">{t('announcement.previewEmpty')}</p>
      )}
    </div>
  );
}

/**
 * Saisie d'un horaire : un jour et une heure, en heure de Paris.
 *
 * Le jour fait partie de la saisie parce qu'il détermine le décalage appliqué
 * (UTC+1 l'hiver, UTC+2 l'été) et le jour que verront les lecteurs des fuseaux
 * éloignés. La valeur produite est un `YYYY-MM-DDTHH:mm` unique.
 */
/**
 * Saisie d'une heure, en `HH:mm`.
 *
 * Champ texte plutôt que `<input type="time">` : le champ natif ne remonte
 * aucune valeur tant que la saisie est incomplète, si bien qu'un composant
 * contrôlé le réécrit à vide en pleine frappe et efface ce qui vient d'être
 * tapé. Ici l'état local suit la frappe, les séparateurs usuels (`h`, `:`) sont
 * acceptés, et seule une heure complète et valide remonte au parent.
 */
function HourInput({
  value,
  onChange,
  id,
}: {
  value: string;
  onChange: (value: string) => void;
  id?: string;
}) {
  const [draft, setDraft] = useState(value);

  // Suit les changements venus du parent (chargement d'un modèle, remise à zéro)
  // sans écraser la frappe en cours : `value` ne bouge que si le parent l'a voulu.
  useEffect(() => {
    setDraft(value);
  }, [value]);

  const handleChange = (raw: string) => {
    const { draft: next, value } = normalizeHourInput(raw);
    setDraft(next);
    onChange(value);
  };

  const invalid = draft.length === 5 && normalizeHourInput(draft).value === '';

  return (
    <div>
      <input
        id={id}
        type="text"
        inputMode="numeric"
        value={draft}
        onChange={(e) => handleChange(e.target.value)}
        placeholder="22:15"
        maxLength={5}
        aria-invalid={invalid}
        className="input w-full"
      />
      {invalid && <p className="text-xs text-red-600 mt-1">00:00 – 23:59</p>}
    </div>
  );
}

/**
 * Aide aux motifs : chaque exemple est calculé par les fonctions de rendu
 * réelles, avec le fuseau du navigateur de l'admin. Le tableau montre donc le
 * format à respecter en saisie *et* ce que le bandeau produira vraiment —
 * y compris le décalage horaire, qu'un exemple écrit en dur masquerait.
 */
function VariablePatternHelp() {
  const { t } = useTranslation('admin');

  const rows = useMemo(() => {
    const values: TemplateValues = {
      texts: { ville: 'Paris' },
      debutDate: EXAMPLE_DAY,
      debutHeure: EXAMPLE_START_HOUR,
      finDate: '',
      finHeure: EXAMPLE_END_HOUR,
    };

    const render = (pattern: string, language: 'fr' | 'en') =>
      localizeTimes(applyVariables(pattern, values, language), language);

    const row = (pattern: string, input: string, help: string) => ({
      pattern,
      input,
      help,
      fr: render(pattern, 'fr'),
      en: render(pattern, 'en'),
    });

    return [
      row('{{text:ville}}', 'Paris', t('announcement.templates.variableTextHelp')),
      row('{{debut:date}}', EXAMPLE_DAY, t('announcement.templates.variableDateHelp')),
      row('{{debut:heure}}', EXAMPLE_START_HOUR, t('announcement.templates.variableTimeHelp')),
      row('{{fin:heure}}', EXAMPLE_END_HOUR, t('announcement.templates.variableEndHelp')),
    ];
  }, [t]);

  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <div className="rounded-lg bg-gray-50 p-3 text-xs text-gray-600 space-y-2">
      <p className="font-medium">{t('announcement.templates.variablesHelp')}</p>

      <div className="overflow-x-auto">
        <table className="w-full text-left border-separate border-spacing-y-1">
          <thead className="text-gray-500">
            <tr>
              <th className="font-medium pr-3">{t('announcement.templates.patternHeader')}</th>
              <th className="font-medium pr-3">{t('announcement.templates.inputHeader')}</th>
              <th className="font-medium">{t('announcement.templates.renderHeader')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.pattern} className="align-top">
                <td className="pr-3 whitespace-nowrap">
                  <code className="font-mono">{row.pattern}</code>
                </td>
                <td className="pr-3 whitespace-nowrap font-mono">{row.input}</td>
                <td>
                  <span className="block" dangerouslySetInnerHTML={{ __html: row.fr }} />
                  <span
                    className="block text-gray-400"
                    dangerouslySetInnerHTML={{ __html: `EN — ${row.en}` }}
                  />
                  <span className="block text-gray-400">{row.help}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p>{t('announcement.templates.timeZoneNote')}</p>
      <p className="text-gray-400">{t('announcement.templates.exampleNote', { zone })}</p>
    </div>
  );
}

export interface TemplateDraft {
  name: string;
  level: AnnouncementLevel;
  contentFr: string;
  contentEn: string;
}

function ModalShell({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-gray-900/50" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-xl shadow-xl p-6 space-y-4"
      >
        <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        {children}
      </div>
    </div>
  );
}

function TemplateEditorModal({
  initial,
  isNew,
  saving,
  translationEnabled,
  onCancel,
  onSave,
  onTranslateError,
}: {
  initial: TemplateDraft;
  isNew: boolean;
  saving: boolean;
  translationEnabled: boolean;
  onCancel: () => void;
  onSave: (draft: TemplateDraft) => void;
  onTranslateError: (message: string) => void;
}) {
  const { t } = useTranslation('admin');
  const [draft, setDraft] = useState<TemplateDraft>(initial);
  const [translating, setTranslating] = useState(false);

  const variables = useMemo(
    () => extractVariables(draft.contentFr, draft.contentEn),
    [draft.contentFr, draft.contentEn]
  );

  const handleTranslate = async () => {
    setTranslating(true);
    try {
      const response = await announcementService.translate(draft.contentFr, 'EN', 'FR');
      if (response.success && response.data) {
        setDraft((prev) => ({ ...prev, contentEn: response.data!.html }));
      }
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      onTranslateError(message || t('announcement.translateError'));
    } finally {
      setTranslating(false);
    }
  };

  return (
    <ModalShell
      title={isNew ? t('announcement.templates.new') : t('announcement.templates.edit')}
      onClose={onCancel}
    >
      <Input
        label={t('announcement.templates.name')}
        value={draft.name}
        onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))}
        placeholder={t('announcement.templates.namePlaceholder')}
      />

      <LevelSelector
        value={draft.level}
        onChange={(level) => setDraft((prev) => ({ ...prev, level }))}
      />

      <div className="space-y-2">
        <label htmlFor="template-fr" className="block text-sm font-medium text-gray-700">
          {t('announcement.contentFr')}
        </label>
        <textarea
          id="template-fr"
          value={draft.contentFr}
          onChange={(e) => setDraft((prev) => ({ ...prev, contentFr: e.target.value }))}
          rows={4}
          className="input w-full font-mono text-sm"
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <label htmlFor="template-en" className="block text-sm font-medium text-gray-700">
            {t('announcement.contentEn')}
          </label>
          {translationEnabled && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleTranslate}
              isLoading={translating}
              disabled={!draft.contentFr.trim() || translating}
              leftIcon={<Languages className="w-4 h-4" />}
            >
              {t('announcement.translate')}
            </Button>
          )}
        </div>
        <textarea
          id="template-en"
          value={draft.contentEn}
          onChange={(e) => setDraft((prev) => ({ ...prev, contentEn: e.target.value }))}
          rows={4}
          className="input w-full font-mono text-sm"
        />
      </div>

      <VariablePatternHelp />

      <p className="text-xs text-gray-600">
        {t('announcement.templates.variables')} :{' '}
        {variables.length > 0
          ? variables.map(describeVariable).join(', ')
          : t('announcement.templates.noVariables')}
      </p>

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>
          {t('announcement.templates.cancel')}
        </Button>
        <Button
          type="button"
          onClick={() => onSave(draft)}
          isLoading={saving}
          disabled={saving || !draft.name.trim() || !draft.contentFr.trim()}
        >
          {t('announcement.templates.saveTemplate')}
        </Button>
      </div>
    </ModalShell>
  );
}

function TemplateFillModal({
  template,
  onCancel,
  onApply,
}: {
  template: AnnouncementTemplate;
  onCancel: () => void;
  onApply: (values: TemplateValues) => void;
}) {
  const { t } = useTranslation('admin');
  const [values, setValues] = useState<TemplateValues>(EMPTY_TEMPLATE_VALUES);

  const variables = template.variables;
  const texts = variables.filter((v) => v.kind === 'text') as { kind: 'text'; name: string }[];
  const period = usesPeriod(variables);
  // Une heure ne peut pas être convertie sans jour : le décalage de Paris
  // dépend de la date. Un modèle qui n'utilise que `{{debut:heure}}` demande
  // donc quand même la date de début.
  const needsDebutDate = period;
  const needsDebutHeure = hasPeriodPart(variables, 'debut', 'heure');
  const needsFin =
    hasPeriodPart(variables, 'fin', 'date') || hasPeriodPart(variables, 'fin', 'heure');
  const needsFinHeure = hasPeriodPart(variables, 'fin', 'heure');

  const setText = (name: string, value: string) =>
    setValues((prev) => ({ ...prev, texts: { ...prev.texts, [name]: value } }));

  const set = <K extends keyof TemplateValues>(key: K, value: TemplateValues[K]) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const previewFr = localizeTimes(applyVariables(template.contentFr, values, 'fr'), 'fr');
  const Icon = levelIcons[template.level];

  return (
    <ModalShell title={`${t('announcement.templates.fill')} — ${template.name}`} onClose={onCancel}>
      {period && (
        <fieldset className="space-y-3 rounded-lg border border-gray-200 p-3">
          <legend className="px-1 text-sm font-medium text-gray-700">
            {t('announcement.templates.period')}
          </legend>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {needsDebutDate && (
              <div>
                <span className="block text-xs text-gray-500 mb-1">
                  {t('announcement.templates.startDate')}
                </span>
                <DateInput
                  value={values.debutDate}
                  onChange={(date) => set('debutDate', date)}
                />
              </div>
            )}
            {needsDebutHeure && (
              <div>
                <label
                  htmlFor="fill-debut-heure"
                  className="block text-xs text-gray-500 mb-1"
                >
                  {t('announcement.templates.startHour')}
                </label>
                <HourInput
                  id="fill-debut-heure"
                  value={values.debutHeure}
                  onChange={(hour) => set('debutHeure', hour)}
                />
              </div>
            )}
          </div>

          {needsFin && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <span className="block text-xs text-gray-500 mb-1">
                    {t('announcement.templates.endDate')}
                  </span>
                  <DateInput
                    value={values.finDate}
                    onChange={(date) => set('finDate', date)}
                    minDate={values.debutDate || undefined}
                  />
                </div>
                {needsFinHeure && (
                  <div>
                    <label
                      htmlFor="fill-fin-heure"
                      className="block text-xs text-gray-500 mb-1"
                    >
                      {t('announcement.templates.endHour')}
                    </label>
                    <HourInput
                      id="fill-fin-heure"
                      value={values.finHeure}
                      onChange={(hour) => set('finHeure', hour)}
                    />
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500">
                {t('announcement.templates.endDateHint')}
              </p>
            </>
          )}
        </fieldset>
      )}

      {texts.length > 0 && (
        <div className="space-y-3">
          {texts.map((variable) => (
            <div key={variable.name} className="space-y-1">
              <label
                htmlFor={`var-${variable.name}`}
                className="block text-sm font-medium text-gray-700"
              >
                {variable.name}
              </label>
              <Input
                id={`var-${variable.name}`}
                value={values.texts[variable.name] || ''}
                onChange={(e) => setText(variable.name, e.target.value)}
              />
            </div>
          ))}
        </div>
      )}

      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-700">{t('announcement.preview')}</p>
        <div className="announcement-banner" data-level={template.level}>
          <Icon className="announcement-banner__icon w-5 h-5" aria-hidden="true" />
          <div
            className="announcement-banner__content"
            // Aperçu local du modèle rempli ; les valeurs saisies sont échappées
            // par applyVariables avant insertion.
            dangerouslySetInnerHTML={{ __html: previewFr }}
          />
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t('announcement.templates.cancel')}
        </Button>
        <Button type="button" onClick={() => onApply(values)}>
          {t('announcement.templates.apply')}
        </Button>
      </div>
    </ModalShell>
  );
}
